import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule, DOCUMENT } from "@angular/common";
import { NgIconComponent } from "@ng-icons/core";
import { isNgIconName } from "../utils/icon";
import { lockBodyScroll, unlockBodyScroll } from "../styles/body-scroll-lock";
import { EvaModalLayer, nextModalZIndex, registerModal, unregisterModal } from "./eva-modal-stack";

/**
 * EVA Dialog - Composant 100% custom
 *
 * Modale de confirmation ou formulaire.
 * Utilise uniquement les tokens EVA.
 *
 * Fonctionnements clés :
 * - S'insère automatiquement au niveau du `<body>` pour éviter les problèmes
 *   de containment CSS (position: fixed dans overflow: auto).
 * - Z-index auto-incrémental : chaque dialog ouverte reçoit un z-index
 *   supérieur, garantissant un empilement correct pour les multi-dialogs.
 *
 * @example
 * // Simple
 * <eva-dialog [(visible)]="isOpen" header="Confirmation">
 *   Contenu de la modale
 * </eva-dialog>
 *
 * // Avec taille prédéfinie
 * <eva-dialog [(visible)]="isOpen" header="Formulaire" size="lg" [showFooter]="false">
 *   <p>Contenu</p>
 * </eva-dialog>
 *
 * // Type confirm (pas de bouton ✕, backdrop non-cliquable, Escape désactivé)
 * <eva-dialog [(visible)]="isOpen" header="Supprimer ?" type="confirm" confirmSeverity="danger"
 *   confirmLabel="Supprimer" (onConfirm)="delete()">
 *   <p>Cette action est irréversible.</p>
 * </eva-dialog>
 *
 * // Avec footer personnalise (type="form" -> pas de boutons par defaut)
 * <eva-dialog [(visible)]="isOpen" header="Formulaire" type="form">
 *   <p>Contenu</p>
 *   <div dialog-footer>
 *     <eva-button label="Annuler" variant="outlined" (click)="isOpen = false"></eva-button>
 *     <eva-button label="Confirmer" (click)="confirm()"></eva-button>
 *   </div>
 * </eva-dialog>
 *
 * Contrat footer (NE PAS CASSER) :
 * - La slot `[dialog-footer]` est TOUJOURS rendue si le parent la projette.
 * - `showFooter` controle UNIQUEMENT l'affichage des boutons par defaut
 *   (Annuler/Confirmer), rendus seulement pour type 'default' | 'confirm' | 'alert'.
 * - Pour type='form', les boutons par defaut ne s'affichent jamais : projeter
 *   un `<div dialog-footer>` pour ajouter les actions. Ne pas ajouter
 *   `[showFooter]="false"` dans ce cas — c'est inutile et historiquement
 *   cassait la projection.
 */
@Component({
  selector: "eva-dialog",
  templateUrl: "./eva-dialog.component.html",
  styleUrls: ["./eva-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, NgIconComponent],
})
export class EvaDialogComponent implements AfterViewInit, OnChanges, EvaModalLayer {
  protected readonly isNgIconName = isNgIconName;
  private el = inject(ElementRef);
  private doc = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Visibilite de la dialog */
  @Input() visible: boolean = false;

  /** Titre */
  @Input() header?: string;

  /** Icone du header */
  @Input() headerIcon?: string;

  /**
   * Afficher le bouton fermer (✕) dans le header.
   * Ignoré pour les types `confirm` et `alert` (bouton ✕ toujours masqué).
   */
  @Input() closable: boolean = true;

  /** Fermer avec Escape (jamais au clic sur le fond) ; ignoré pour les types confirm et alert */
  @Input() dismissible: boolean = true;

  /** Bouton du header pour passer la dialog en plein écran et revenir */
  @Input() maximizable: boolean = false;

  /** Afficher le footer par defaut */
  @Input() showFooter: boolean = true;

  /** Label du bouton confirmer */
  @Input() confirmLabel: string = "Confirmer";

  /** Label du bouton annuler */
  @Input() cancelLabel: string = "Annuler";

  /** Variante du bouton confirmer */
  @Input() confirmSeverity: "primary" | "success" | "warning" | "danger" = "primary";

  /** Desactiver le bouton confirmer */
  @Input() confirmDisabled: boolean = false;

  /** Afficher le loader sur confirmer */
  @Input() confirmLoading: boolean = false;

  /**
   * Largeur explicite de la dialog (ex: "600px", "80vw").
   * Ignoré si `size` est défini.
   */
  @Input() width: string = "500px";

  /** Largeur maximale */
  @Input() maxWidth: string = "90vw";

  /** Hauteur fixe de la dialog (ex: "90vh"), plafonnée à la hauteur du viewport */
  @Input() height?: string;

  /** Styles inline de la zone de contenu (ex: { padding: '0' }) */
  @Input() contentStyle?: { [key: string]: string };

  /**
   * Taille prédéfinie — remplace `width` si spécifié.
   * - sm : 400px  — confirmations simples
   * - md : 600px  — formulaires courts
   * - lg : 800px  — formulaires larges / 2 colonnes
   * - xl : 1100px — wizards / interfaces complexes
   */
  @Input() size?: "sm" | "md" | "lg" | "xl";

  /** Bloquer le scroll du body */
  @Input() blockScroll: boolean = true;

  /** Centrer verticalement */
  @Input() centered: boolean = true;

  /** Classe CSS personnalisee */
  @Input() styleClass: string = "";

  /** Mode plein écran (sans padding, sans border-radius) */
  @Input() fullscreen: boolean = false;

  /** Type de dialog */
  @Input() type: "default" | "confirm" | "alert" | "form" = "default";

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Liaison two-way sur la visibilité de la dialog — émet la nouvelle valeur booléenne */
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Émet quand la dialog devient visible (après ouverture) */
  @Output() onShow = new EventEmitter<void>();

  /** Émet quand la dialog se ferme (après masquage) */
  @Output() onHide = new EventEmitter<void>();

  /** Émet quand l'utilisateur clique sur le bouton de confirmation */
  @Output() onConfirm = new EventEmitter<void>();

  /** Émet quand l'utilisateur clique sur le bouton d'annulation */
  @Output() onCancel = new EventEmitter<void>();

  // ==========================================================================
  // State
  // ==========================================================================

  private isAnimating: boolean = false;

  private isOpen: boolean = false;

  /** Plein écran demandé par l'utilisateur via le bouton `maximizable` */
  maximized: boolean = false;

  /** Z-index courant de cette instance (assigné à l'ouverture) */
  currentZIndex: number = 1200;

  /**
   * Garde posée par le contenu (ex. formulaire non enregistré) : consultée avant une fermeture
   * demandée par l'utilisateur (✕, Escape, Annuler). Une fermeture pilotée par le parent
   * via `[visible]` ne passe pas par elle.
   */
  private closeGuard: (() => boolean | Promise<boolean>) | null = null;

  private readonly sizeMap: Record<string, string> = {
    sm: "400px",
    md: "600px",
    lg: "800px",
    xl: "1100px",
  };

  constructor(private cdr: ChangeDetectorRef) {
    // Pas en ngAfterViewInit : jamais appelé si le contenu plante, la dialog resterait dans la pile.
    this.destroyRef.onDestroy(() => {
      this.unregisterOpen();
      const el = this.el.nativeElement;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  /**
   * Déplace le host element au niveau du body pour éviter les problèmes de
   * containment CSS : `position: fixed` imbriqué dans `overflow: auto`
   * (typiquement un dialog fullscreen) est contenu par le parent dans Chrome.
   * En appending au body, le dialog est toujours au niveau du viewport.
   */
  ngAfterViewInit(): void {
    this.doc.body.appendChild(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const visibleChange = changes["visible"];
    if (!visibleChange) return;
    // Émission différée : on est dans la détection du parent, un handler qui le modifie ferait NG0100.
    if (visibleChange.currentValue) {
      this.handleOpen(true);
    } else {
      this.handleClose(true);
    }
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  /** Appelé pour la dialog au sommet de la pile quand l'utilisateur presse Escape. */
  closeFromEscape(): void {
    if (this.visible && this.dismissible && this.type !== "confirm" && this.type !== "alert") {
      this.requestHide();
    }
  }

  setCloseGuard(guard: (() => boolean | Promise<boolean>) | null): void {
    this.closeGuard = guard;
  }

  /** Fermeture demandée par l'utilisateur : passe par la garde du contenu si elle existe. */
  async requestHide(): Promise<void> {
    if (this.closeGuard && !(await this.closeGuard())) return;
    this.hide();
  }

  show(): void {
    if (this.visible || this.isAnimating) return;

    this.doc.body.appendChild(this.el.nativeElement);

    this.visible = true;
    this.visibleChange.emit(true);
    this.handleOpen();

    this.cdr.markForCheck();
  }

  hide(): void {
    if (!this.visible || this.isAnimating) return;

    this.isAnimating = true;
    this.visible = false;
    this.visibleChange.emit(false);
    this.handleClose();

    setTimeout(() => {
      this.isAnimating = false;
      this.cdr.markForCheck();
    }, 200);

    this.cdr.markForCheck();
  }

  confirm(): void {
    if (this.confirmDisabled || this.confirmLoading) return;
    this.onConfirm.emit();
  }

  cancel(): void {
    this.onCancel.emit();
    this.requestHide();
  }

  toggleMaximize(): void {
    this.maximized = !this.maximized;
    this.cdr.markForCheck();
  }

  onContentClick(event: Event): void {
    event.stopPropagation();
  }

  private handleOpen(deferEmit = false): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.maximized = false;
    this.currentZIndex = nextModalZIndex(this, this.el.nativeElement, this.doc);
    registerModal(this, this.doc);
    if (this.blockScroll) lockBodyScroll(this);
    this.emit(this.onShow, deferEmit);
  }

  private handleClose(deferEmit = false): void {
    if (!this.isOpen) return;
    this.unregisterOpen();
    this.emit(this.onHide, deferEmit);
  }

  private emit(emitter: EventEmitter<void>, defer: boolean): void {
    if (defer) {
      Promise.resolve().then(() => emitter.emit());
    } else {
      emitter.emit();
    }
  }

  private unregisterOpen(): void {
    this.isOpen = false;
    unlockBodyScroll(this);
    unregisterModal(this);
  }

  get dialogStyle(): { [key: string]: string } {
    const w = this.size ? this.sizeMap[this.size] : this.width;
    const style: { [key: string]: string } = {
      width: w,
      maxWidth: this.maxWidth,
    };
    if (this.height && !this.maximized) {
      style["height"] = this.height;
    }
    return style;
  }

  get typeIcon(): string {
    switch (this.type) {
      case "confirm":
        return "heroQuestionMarkCircle";
      case "alert":
        return "heroExclamationTriangle";
      default:
        return "";
    }
  }
}
